import { CONNECTION_FEE_MAX_CENTS, isValidConnectionFeeCents } from '@evi/shared';

/**
 * The charger connection-fee field (ChargerDetailPage, Network Origin
 * block): edited in dollars, stored as integer cents on
 * Charger.connection_fee_cents. Blank means null, which means no fee.
 *
 * Parsed from the raw text, never through `Number()` on a
 * type="number" input: a browser hands back '' for malformed number
 * input, which would read as "no fee" and silently drop EVI's fee on a
 * typo. Here a malformed value is an error the form refuses to save.
 * Cents are built from the digit groups, so "1.10" is exactly 110 with
 * no floating-point rounding.
 */
export type ConnectionFeeInput =
  | { ok: true; cents: number | null }
  | { ok: false; message: string };

const MAX_LABEL = `$${(CONNECTION_FEE_MAX_CENTS / 100).toFixed(2)}`;

export function parseConnectionFeeDollars(input: string): ConnectionFeeInput {
  const raw = input.trim();
  if (raw === '') return { ok: true, cents: null };
  // A leading "$" is allowed; a bare "$" is not blank, it is malformed.
  const t = raw.replace(/^\$\s*/, '');
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(t);
  if (!m || (m[1] === '' && !m[2])) {
    return { ok: false, message: 'Enter dollars and cents, e.g. 1.00' };
  }
  const cents = Number(m[1] || '0') * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  if (!isValidConnectionFeeCents(cents)) {
    return { ok: false, message: `$0.00 to ${MAX_LABEL}` };
  }
  return { ok: true, cents };
}

/** Stored cents to the field's text: 100 → "1.00", null → "". */
export function connectionFeeCentsToDollars(cents: number | null | undefined): string {
  return typeof cents === 'number' ? (cents / 100).toFixed(2) : '';
}

/** A stored fee for display: 100 → "$1.00", null/absent → "no fee". */
export function formatConnectionFee(cents: number | null | undefined): string {
  return typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : 'no fee';
}
